import { Flex, Text } from '@chakra-ui/react';
import { FC } from 'react';
import { DocumentActionButtons } from './DocumentActionButtons';

interface DocumentTitleBarProps {
  /** The active checklist item title — the page heading. */
  title: string;
  /**
   * True when the checklist item needs review (any of its documents do). Drives
   * the "Ignore and resubmit" action and the "Fix" vs "Open" SkySlope label.
   */
  needsReview: boolean;
  /** True once the checklist item has been resubmitted (disables the action). */
  isResubmitted: boolean;
  /** True while the resubmit mutation is in flight. */
  isSubmitting: boolean;
  /**
   * True once every tab's detail has loaded — the resubmit payload + note are
   * derived from all of them, so the trigger waits for it.
   */
  isResubmitReady: boolean;
  /** The note the resubmit modal is prepopulated with (grouped by file name). */
  defaultResubmitMessage: string;
  /** SkySlope (or other platform) deep link for the folder/document. */
  platformUrl?: string | null;
  /** Fires the checklist-level resubmit with the agent's note. */
  onResubmit: (message: string) => void | Promise<void>;
}

/**
 * Center title bar: the active checklist item name on the left, the
 * checklist-level actions ("Ignore and resubmit" + "Fix in SkySlope") on the
 * right. The actions act on the whole checklist item (every document tab), which
 * is why they live here rather than per-document.
 */
export const DocumentTitleBar: FC<DocumentTitleBarProps> = ({
  title,
  needsReview,
  isResubmitted,
  isSubmitting,
  isResubmitReady,
  defaultResubmitMessage,
  platformUrl,
  onResubmit,
}) => (
  <Flex
    align="center"
    borderBottomColor="compliance.border.primary"
    borderBottomWidth={1}
    flexShrink={0}
    gap={4}
    justify="space-between"
    minH="64px"
    px={6}
    py={3}
    w="full"
  >
    <Text
      color="compliance.text.primary"
      fontSize="lg"
      fontWeight="semibold"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {title}
    </Text>

    <DocumentActionButtons
      defaultResubmitMessage={defaultResubmitMessage}
      isResubmitReady={isResubmitReady}
      isResubmitted={isResubmitted}
      isSubmitting={isSubmitting}
      needsReview={needsReview}
      platformUrl={platformUrl}
      onResubmit={onResubmit}
    />
  </Flex>
);
